/**
 * PhotoUploader Component
 * Drag-and-drop file upload with file validation and progress tracking
 * Supports multiple file selection and album selection
 *
 * @module components/gallery/PhotoUploader
 */

import { useRef, useEffect, useState } from 'react';
import { UploadProgress, Album } from '@/types';
import { logger } from '@/utils/logger';
import { PHOTO_CONFIG } from '@/utils/constants';

// ============================================================================
// TYPES
// ============================================================================

interface PhotoUploaderProps {
  onFilesSelected: (files: File[]) => void;
  onUploadComplete?: (photoIds: string[]) => void;
  isUploading?: boolean;
  uploadProgress?: UploadProgress[];
  albums?: Album[];
  selectedAlbumId?: string;
  onAlbumChange?: (albumId: string | undefined) => void;
  allowMultiple?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Photo uploader component with drag-and-drop and file selection
 * Validates file type and size before submission
 * Shows upload progress for each file
 * Optional album selection for organizing uploads
 *
 * @component
 * @example
 * <PhotoUploader
 *   onFilesSelected={handleFilesSelected}
 *   onUploadComplete={handleUploadComplete}
 *   isUploading={isUploading}
 *   uploadProgress={progress}
 *   albums={albums}
 * />
 */
const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  onFilesSelected,
  onUploadComplete,
  isUploading = false,
  uploadProgress = [],
  albums = [],
  selectedAlbumId,
  onAlbumChange,
  allowMultiple = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Debug logging
  useEffect(() => {
    logger.debug(`[WALRUS] PhotoUploader: mounted`, {
      allowMultiple,
      albumCount: albums.length,
    });
  }, [allowMultiple, albums.length]);

  // Check if all uploads completed successfully
  useEffect(() => {
    if (!isUploading && uploadProgress.length > 0) {
      const allComplete = uploadProgress.every((p) => p.status === 'success');
      const hasErrors = uploadProgress.some((p) => p.status === 'error');

      if (allComplete && !hasErrors && onUploadComplete) {
        logger.debug(`[WALRUS] PhotoUploader: all uploads completed successfully`);
        // Extract photo IDs from completed uploads (would be set by parent)
        setPreviewFiles([]);
        setErrors([]);
      }
    }
  }, [isUploading, uploadProgress, onUploadComplete]);

  /**
   * Validate files for type and size
   */
  const validateFiles = (files: File[]): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];

    logger.debug(`[WALRUS] PhotoUploader: validating ${files.length} files`);

    files.forEach((file, index) => {
      // Check file type
      if (!PHOTO_CONFIG.allowedTypes.includes(file.type as any)) {
        errors.push(
          `File ${index + 1} (${file.name}): Invalid file type. Allowed: ${PHOTO_CONFIG.allowedTypes.join(', ')}`
        );
        logger.warn(`[WALRUS] PhotoUploader: invalid file type`, {
          filename: file.name,
          type: file.type,
        });
        return;
      }

      // Check file size
      if (file.size > PHOTO_CONFIG.maxFileSize) {
        errors.push(
          `File ${index + 1} (${file.name}): File size exceeds ${PHOTO_CONFIG.maxFileSize / 1024 / 1024}MB limit`
        );
        logger.warn(`[WALRUS] PhotoUploader: file too large`, {
          filename: file.name,
          size: file.size,
          maxSize: PHOTO_CONFIG.maxFileSize,
        });
        return;
      }

      valid.push(file);
    });

    logger.debug(`[WALRUS] PhotoUploader: validation complete`, {
      validCount: valid.length,
      errorCount: errors.length,
    });

    return { valid, errors };
  };

  /**
   * Handle files selected from input or drag-drop
   */
  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const { valid, errors } = validateFiles(fileArray);

    if (errors.length > 0) {
      setErrors(errors);
      logger.warn(`[WALRUS] PhotoUploader: validation errors found`, { errors });
      return;
    }

    // Clear previous errors if new files are valid
    setErrors([]);

    // Handle single vs multiple files
    const filesToAdd = allowMultiple ? valid : [valid[0]].filter(Boolean);

    logger.debug(`[WALRUS] PhotoUploader: files selected`, {
      count: filesToAdd.length,
      allowMultiple,
    });

    setPreviewFiles(filesToAdd);
    onFilesSelected(filesToAdd);
  };

  /**
   * Handle drag events
   */
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
      logger.debug(`[WALRUS] PhotoUploader: drag over`);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
      logger.debug(`[WALRUS] PhotoUploader: drag leave`);
    }
  };

  /**
   * Handle drop event
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    logger.debug(`[WALRUS] PhotoUploader: drop event`, {
      fileCount: e.dataTransfer.files.length,
    });

    handleFiles(e.dataTransfer.files);
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logger.debug(`[WALRUS] PhotoUploader: input change`);
    handleFiles(e.target.files);
  };

  /**
   * Handle click on upload area
   */
  const handleClick = () => {
    logger.debug(`[WALRUS] PhotoUploader: click on upload area`);
    inputRef.current?.click();
  };

  /**
   * Remove a file from preview
   */
  const handleRemoveFile = (index: number) => {
    logger.debug(`[WALRUS] PhotoUploader: removing file`, { index });
    setPreviewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Handle album change
   */
  const handleAlbumChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || undefined;
    logger.debug(`[WALRUS] PhotoUploader: album changed`, { albumId: value });
    onAlbumChange?.(value);
  };

  return (
    <div className="space-y-4">
      {/* Drag and drop area */}
      <div
        className={`rounded-lg border-2 border-dashed transition-all duration-200 ${
          dragActive
            ? 'border-walrus-black bg-walrus-cream'
            : 'border-walrus-gray bg-walrus-white'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={allowMultiple}
          accept={PHOTO_CONFIG.allowedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          aria-label="Upload photos"
        />

        <div className="flex flex-col items-center justify-center px-6 py-12">
          <svg
            className="mb-4 h-12 w-12 text-walrus-silver"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33A3 3 0 0116.5 19.5H6.75z"
            />
          </svg>

          <p className="text-center text-walrus-black">
            <span className="font-semibold">Click to upload</span> or{' '}
            <span className="font-semibold">drag and drop</span>
          </p>
          <p className="text-sm text-walrus-silver">
            {PHOTO_CONFIG.allowedExtensions.map((ext) => ext.toUpperCase()).join(', ')} up to{' '}
            {PHOTO_CONFIG.maxFileSize / 1024 / 1024}MB
          </p>
        </div>
      </div>

      {/* Error messages */}
      {errors.length > 0 && (
        <div className="rounded-lg bg-red-100 border border-red-400 p-4">
          <h4 className="mb-2 font-semibold text-red-700">Upload Errors:</h4>
          <ul className="space-y-1">
            {errors.map((error, index) => (
              <li key={index} className="text-sm text-red-600">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Album selector */}
      {albums.length > 0 && (
        <div>
          <label htmlFor="album-select" className="block text-sm font-medium text-walrus-black mb-2">
            Album (optional)
          </label>
          <select
            id="album-select"
            value={selectedAlbumId || ''}
            onChange={handleAlbumChange}
            className="w-full rounded-lg bg-walrus-white border border-walrus-gray text-walrus-black px-3 py-2 transition-colors hover:border-walrus-silver focus:outline-none focus:ring-2 focus:ring-walrus-black"
          >
            <option value="">No Album</option>
            {albums.map((album) => (
              <option key={album.id} value={album.id}>
                {album.name} ({album.photoCount} photos)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Preview files */}
      {previewFiles.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-walrus-black">
            {previewFiles.length} {previewFiles.length === 1 ? 'file' : 'files'} selected
          </h4>
          <div className="space-y-2">
            {previewFiles.map((file, index) => {
              const progress = uploadProgress.find((p) => p.filename === file.name);
              const isProcessing = progress && progress.status !== 'pending';

              return (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg bg-walrus-white p-3 border border-walrus-gray"
                >
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-walrus-black text-sm">
                      {file.name}
                    </p>
                    <p className="text-xs text-walrus-silver">
                      {(file.size / 1024 / 1024).toFixed(2)}MB
                    </p>

                    {/* Upload progress bar */}
                    {isProcessing && (
                      <div className="mt-2 w-full bg-walrus-gray rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-200 ${
                            progress?.status === 'success'
                              ? 'bg-green-500'
                              : progress?.status === 'error'
                                ? 'bg-red-500'
                                : 'bg-walrus-black'
                          }`}
                          style={{ width: `${progress?.progress || 0}%` }}
                        />
                      </div>
                    )}

                    {/* Status message */}
                    {progress?.status === 'error' && (
                      <p className="mt-1 text-xs text-red-600">{progress.error}</p>
                    )}
                  </div>

                  {/* Status icon or remove button */}
                  {!isProcessing && (
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="ml-3 rounded-lg p-1 text-walrus-silver transition-colors hover:bg-walrus-cream hover:text-walrus-black"
                      title="Remove file"
                      aria-label="Remove file"
                      type="button"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Current upload progress display */}
      {uploadProgress.length > 0 && isUploading && (
        <div className="rounded-lg bg-walrus-white border border-walrus-gray p-4">
          <h4 className="mb-3 font-medium text-walrus-black">Uploading...</h4>
          <div className="space-y-2">
            {uploadProgress.map((progress) => (
              <div key={progress.filename} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate text-walrus-black">{progress.filename}</span>
                  <span className="text-walrus-silver">{progress.progress}%</span>
                </div>
                <div className="h-2 rounded-full bg-walrus-gray overflow-hidden">
                  <div
                    className={`h-full transition-all duration-200 ${
                      progress.status === 'success'
                        ? 'bg-green-500'
                        : progress.status === 'error'
                          ? 'bg-red-500'
                          : 'bg-walrus-black'
                    }`}
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

PhotoUploader.displayName = 'PhotoUploader';

export default PhotoUploader;
